// WhaleWatcher.js - Whale Watcher (13F Institutional Holdings)
import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  RefreshCw,
  Loader,
  AlertTriangle,
  Briefcase,
  Users,
  Search,
  DollarSign,
  PieChart,
  Star,
  ChevronDown,
  Info,
  Eye
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const WhaleWatcher = () => {
  const [activeTab, setActiveTab] = useState('whales');
  const [whales, setWhales] = useState([]);
  const [selectedWhale, setSelectedWhale] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [popularStocks, setPopularStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHoldings, setLoadingHoldings] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [whaleStats, setWhaleStats] = useState(null);

  // Search
  const [searchSymbol, setSearchSymbol] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    fetchWhales();
  }, []);

  const fetchWhales = async () => {
    setLoading(true);
    setError(null);

    try {
      const [whalesRes, popularRes] = await Promise.all([
        fetch(`${API_BASE}/whale/list`),
        fetch(`${API_BASE}/whale/popular?limit=15`)
      ]);

      if (whalesRes.ok) {
        const data = await whalesRes.json();
        setWhales(data.whales || []);
      }

      if (popularRes.ok) {
        const data = await popularRes.json();
        setPopularStocks(data.stocks || []);
      }

    } catch (err) {
      console.error('Error fetching whales:', err);
      setError('Failed to load whale data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHoldings = async (cik, reset = true) => {
    setLoadingHoldings(true);
    
    try {
      const pageNum = reset ? 1 : page + 1;
      const res = await fetch(`${API_BASE}/whale/holdings/${cik}?page=${pageNum}&limit=30`);
      
      if (res.ok) {
        const data = await res.json();
        if (reset) {
          setHoldings(data.holdings || []);
        } else {
          setHoldings(prev => [...prev, ...(data.holdings || [])]);
        }
        setWhaleStats({
          totalValue: data.totalValue,
          totalPositions: data.totalPositions,
          top10Concentration: data.top10Concentration
        });
        setHasMore(data.hasMore || false);
        setPage(pageNum);
      }
    } catch (err) {
      console.error('Error fetching holdings:', err);
    } finally {
      setLoadingHoldings(false);
    }
  };

  const selectWhale = (whale) => {
    setSelectedWhale(whale);
    setPage(1);
    setHoldings([]);
    fetchHoldings(whale.cik, true);
    setActiveTab('holdings');
  };

  const searchStock = async () => {
    if (!searchSymbol.trim()) return;
    setLoading(true);
    
    try {
      const res = await fetch(`${API_BASE}/whale/compare?symbols=${searchSymbol.toUpperCase()}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
        setActiveTab('search');
      }
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value) => {
    if (!value) return '$0';
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(2)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatShares = (shares) => {
    if (!shares) return '0';
    if (shares >= 1000000) return `${(shares / 1000000).toFixed(2)}M`;
    if (shares >= 1000) return `${(shares / 1000).toFixed(1)}K`;
    return shares.toLocaleString();
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c2d12 0%, #c2410c 50%, #fb923c 100%)',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-30px',
          right: '-30px',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Briefcase size={28} />
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Whale Watcher</h1>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '0.9rem' }}>
                Track what billionaire investors are holding • 13F filings
              </p>
            </div>
            <button
              onClick={fetchWhales}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
            >
              {loading ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(251, 146, 60, 0.1)',
        border: '1px solid rgba(251, 146, 60, 0.3)',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <Info size={20} color="#fb923c" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{ color: '#fdba74', fontSize: '0.85rem', margin: '0 0 0.5rem 0', fontWeight: '600' }}>
            What are 13F Filings?
          </p>
          <p style={{ color: '#fed7aa', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
            Institutional investors managing $100M+ must disclose holdings quarterly via SEC Form 13F.
            See what Buffett, Dalio, and other legendary investors own.
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '0.75rem',
        border: '1px solid #334155'
      }}>
        <input
          type="text"
          value={searchSymbol}
          onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
          onKeyPress={(e) => e.key === 'Enter' && searchStock()}
          placeholder="Search which whales own a stock (e.g., AAPL)"
          style={{
            flex: 1,
            padding: '0.75rem',
            background: 'rgba(15, 23, 42, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.5rem',
            color: 'white',
            fontSize: '0.9rem'
          }}
        />
        <button
          onClick={searchStock}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: '#c2410c',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          <Search size={18} />
          Search
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto' }}>
        {[
          { id: 'whales', label: 'Famous Investors', icon: Users },
          { id: 'popular', label: 'Popular Holdings', icon: Star },
          { id: 'holdings', label: selectedWhale ? `${selectedWhale.name}` : 'Holdings', icon: PieChart, disabled: !selectedWhale },
          { id: 'search', label: 'Search Results', icon: Search, disabled: !searchResults }
        ].filter(t => !t.disabled || t.id === 'holdings' && selectedWhale || t.id === 'search' && searchResults).map(tab => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: activeTab === tab.id ? 'rgba(251, 146, 60, 0.2)' : 'rgba(30, 41, 59, 0.5)',
              border: activeTab === tab.id ? '1px solid rgba(251, 146, 60, 0.4)' : '1px solid #334155',
              borderRadius: '0.5rem',
              color: activeTab === tab.id ? '#fb923c' : tab.disabled ? '#475569' : '#94a3b8',
              cursor: tab.disabled ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              opacity: tab.disabled ? 0.5 : 1
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Loader size={48} color="#fb923c" className="spin" />
        </div>
      ) : error ? (
        <div style={{ padding: '2rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.75rem', textAlign: 'center' }}>
          <p style={{ color: '#fca5a5' }}>{error}</p>
          <button onClick={fetchWhales} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#c2410c', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      ) : (
        <>
          {/* Famous Investors Tab */}
          {activeTab === 'whales' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
              {whales.map((whale, idx) => (
                <div
                  key={idx}
                  onClick={() => selectWhale(whale)}
                  style={{
                    background: 'rgba(30, 41, 59, 0.5)',
                    borderRadius: '0.75rem',
                    padding: '1.25rem',
                    border: '1px solid #334155',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    ':hover': { borderColor: '#fb923c' }
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#fb923c'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#334155'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #c2410c, #fb923c)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      color: 'white'
                    }}>
                      {whale.manager?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '1rem', color: '#f8fafc' }}>{whale.name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#fb923c' }}>{whale.manager}</div>
                    </div>
                    <Eye size={20} color="#64748b" />
                  </div>
                  <div style={{
                    padding: '0.5rem 0.75rem',
                    background: 'rgba(251, 146, 60, 0.1)',
                    borderRadius: '0.5rem',
                    fontSize: '0.75rem',
                    color: '#fdba74'
                  }}>
                    {whale.style}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Popular Holdings Tab */}
          {activeTab === 'popular' && (
            <div>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Stocks held by multiple legendary investors
              </p>
              
              <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', border: '1px solid #334155', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>#</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Symbol</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>Whale Holders</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.75rem' }}>Total Value</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Top Holders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popularStocks.map((stock, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '0.75rem', color: '#64748b', fontSize: '0.85rem' }}>{idx + 1}</td>
                          <td style={{ padding: '0.75rem' }}>
                            <span style={{ fontWeight: '600', fontSize: '1rem', color: '#60a5fa' }}>{stock.symbol}</span>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              background: stock.holderCount >= 5 ? 'rgba(251, 146, 60, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                              borderRadius: '1rem',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              color: stock.holderCount >= 5 ? '#fb923c' : '#c4b5fd'
                            }}>
                              {stock.holderCount} whales
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#22c55e' }}>
                            {formatValue(stock.totalValue)}
                          </td>
                          <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                            {stock.topHolders?.slice(0, 3).join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Holdings Tab */}
          {activeTab === 'holdings' && selectedWhale && (
            <div>
              {/* Whale Stats */}
              {whaleStats && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Portfolio Value</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#22c55e' }}>{formatValue(whaleStats.totalValue)}</div>
                  </div>
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Positions</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#60a5fa' }}>{whaleStats.totalPositions}</div>
                  </div>
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Top 10 Concentration</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fb923c' }}>{whaleStats.top10Concentration?.toFixed(1)}%</div>
                  </div>
                </div>
              )}

              {loadingHoldings ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                  <Loader size={32} color="#fb923c" className="spin" />
                </div>
              ) : (
                <>
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', border: '1px solid #334155', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #334155' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>#</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Symbol</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Name</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.75rem' }}>Shares</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.75rem' }}>Value</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.75rem' }}>Portfolio %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {holdings.map((h, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                              <td style={{ padding: '0.75rem', color: '#64748b', fontSize: '0.85rem' }}>{idx + 1}</td>
                              <td style={{ padding: '0.75rem' }}>
                                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#60a5fa' }}>{h.symbol}</span>
                              </td>
                              <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: '#94a3b8', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {h.name}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.85rem' }}>{formatShares(h.shares)}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#22c55e' }}>{formatValue(h.value)}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                <span style={{
                                  padding: '0.25rem 0.5rem',
                                  background: parseFloat(h.portfolioWeight) >= 5 ? 'rgba(251, 146, 60, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.8rem',
                                  color: parseFloat(h.portfolioWeight) >= 5 ? '#fb923c' : '#94a3b8'
                                }}>
                                  {h.portfolioWeight}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {hasMore && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                      <button
                        onClick={() => fetchHoldings(selectedWhale.cik, false)}
                        disabled={loadingHoldings}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem 2rem',
                          background: 'rgba(251, 146, 60, 0.2)',
                          border: '1px solid rgba(251, 146, 60, 0.4)',
                          borderRadius: '0.5rem',
                          color: '#fb923c',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}
                      >
                        <ChevronDown size={18} />
                        Load More
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Search Results Tab */}
          {activeTab === 'search' && searchResults && (
            <div>
              {searchResults.map((result, idx) => (
                <div key={idx} style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: '0.75rem',
                  padding: '1.25rem',
                  border: '1px solid #334155',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#60a5fa', marginBottom: '1rem' }}>
                    {result.symbol}
                  </h3>
                  
                  {result.holders && result.holders.length > 0 ? (
                    <div>
                      <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.75rem' }}>
                        Held by {result.holders.length} whale{result.holders.length > 1 ? 's' : ''}:
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.75rem' }}>
                        {result.holders.map((holder, hIdx) => (
                          <div key={hIdx} style={{
                            padding: '0.75rem',
                            background: 'rgba(15, 23, 42, 0.5)',
                            borderRadius: '0.5rem',
                            border: '1px solid #1e293b'
                          }}>
                            <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.25rem' }}>{holder.whale}</div>
                            <div style={{ fontSize: '0.75rem', color: '#fb923c', marginBottom: '0.5rem' }}>{holder.manager}</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                              <span style={{ color: '#64748b' }}>Value:</span>
                              <span style={{ color: '#22c55e', fontWeight: '600' }}>{formatValue(holder.value)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                              <span style={{ color: '#64748b' }}>Shares:</span>
                              <span style={{ color: '#e2e8f0' }}>{formatShares(holder.shares)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: '#64748b', fontStyle: 'italic' }}>
                      No whales currently holding this stock (in our tracked list)
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Disclaimer */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: '0.75rem',
        marginTop: '1.5rem'
      }}>
        <AlertTriangle size={20} color="#f87171" style={{ flexShrink: 0, marginTop: '2px' }} />
        <p style={{ color: '#fca5a5', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
          <strong>Disclaimer:</strong> 13F filings are delayed 45 days after quarter end.
          Holdings may have changed since filing. This is not investment advice.
        </p>
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default WhaleWatcher;
